import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Check } from '@phosphor-icons/react';
import type { DeliverableInstance } from '@discovr/types';
import { isMain, isAddon, fmtDeliverableType } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { formatGoTotal, formatLineMoney } from '../../../lib/formatMoney';
import { rowSelectedTotal } from './helpers';
import { toast } from 'sonner';
import { useCampaignCapabilitiesStore } from '@discovr/features';
import type { PendingPublicAction } from '@discovr/common';
import { useDeliverableSelection } from './useDeliverableSelection';
import { SHOW_DELIVERABLE_ADDONS } from './types';
import type { CreatorsRow } from './types';

export interface CommercialPopoverState {
  creatorId: string;
  rect: DOMRect;
  /** Trigger element — used to recompute position as the page scrolls. */
  triggerEl: HTMLElement;
}

interface CommercialPopoverProps {
  popover: CommercialPopoverState;
  rows: CreatorsRow[];
  popoverRef: React.RefObject<HTMLDivElement | null>;
  /** Campaign platform — drives isMain/isAddon classification. */
  platform: string;
  /** True on a public (no-login) share link. */
  isPublicView?: boolean;
  /**
   * The share link's short code + path-tail (from `usePublicView()`,
   * threaded down via CreatorsTab) — stamped onto the `save_deliverables`
   * pending action below so it can be resumed against the SAME link after
   * sign-in. Empty/undefined outside public mode, where they're unused.
   */
  campCode?: string;
  tail?: string;
  /** Opens the sign-in dialog — called instead of saving when a public
   *  visitor clicks Save (2026-08-06 decision). On a public link, called
   *  with a `save_deliverables` PendingPublicAction built from the popover's
   *  current selection so the write can be resumed after sign-in. */
  requestLogin?: (action?: PendingPublicAction) => void;
  selectCreatorDeliverable?: ((entryId: string, types: string[]) => Promise<void>) | null;
  onOptimisticChange: (creatorId: string, selectedTypes: string[]) => void;
  onClose: () => void;
}

export function CommercialPopover({
  popover,
  rows,
  popoverRef,
  platform,
  isPublicView,
  campCode,
  tail,
  requestLogin,
  selectCreatorDeliverable,
  onOptimisticChange,
  onClose,
}: CommercialPopoverProps) {
  const pc = rows.find((c) => c.id === popover.creatorId);

  // An AUTHED read-only member (accessLevel: 'read' but not a public link —
  // e.g. brand_member without deliverable-selection permission) may open the
  // popover and see deliverables + prices, but cannot change the selection
  // or save — there's no sign-in prompt to offer someone already signed in.
  // A public visitor also carries accessLevel: 'read' (see
  // PublicShareViewPage's PUBLIC_READ_ONLY_CAPABILITIES) but is NOT held to
  // this gate: they can select freely, and Save prompts sign-in instead of
  // being hidden (2026-08-06 decision — see the Save button below).
  const accessLevel = useCampaignCapabilitiesStore((s) => s.accessLevel);
  const isAuthedReadOnly = accessLevel === 'read' && !isPublicView;

  // Hook must be called unconditionally before any early return — Rules of Hooks.
  // Empty array when pc is not found; the guard below prevents render.
  const deliverables = pc?.deliverables ?? [];
  const {
    selectedMain,
    selectedAddons,
    toggleMain,
    toggleAddon,
    selectedTypes,
    isAddonDisabled,
  } = useDeliverableSelection(deliverables, platform);

  // Go prices the saved selection only; a selection being edited shows no total until it is saved.
  const persistedTypes = deliverables.filter((d) => d.is_selected).map((d) => d.type);
  const selectionUnchanged = persistedTypes.length === selectedTypes.length && persistedTypes.every((t) => selectedTypes.includes(t));
  const saved = pc ? rowSelectedTotal(pc) : null;
  const totalLabel =
    selectionUnchanged && saved?.total != null && saved.total > 0
      ? `Total: ${formatGoTotal(saved.total, pc?.money ?? { currency: 'INR' })}${saved.note ? ` ${saved.note}` : ''}`
      : '';


  // ── Live rect tracking: follow the trigger row as the page scrolls ────────
  const [liveRect, setLiveRect] = useState<DOMRect>(() => popover.rect);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const el = popover.triggerEl;
    const update = () => setLiveRect(el.getBoundingClientRect());
    // capture:true catches scroll inside the table container, not just window
    window.addEventListener('scroll', update, { capture: true, passive: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', update);
    };
  }, [popover.triggerEl]);

  // Flip the panel above the row when there's not enough space below.
  // useLayoutEffect prevents the one-frame flicker of rendering below then jumping above.
  useLayoutEffect(() => {
    if (!popoverRef.current) return;
    setFlipped(window.innerHeight - liveRect.bottom < popoverRef.current.offsetHeight + 8);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveRect]);

  // Clamp left so the panel never overflows the right edge of the viewport
  const safeLeft = Math.min(liveRect.left, window.innerWidth - 296);
  const posStyle: React.CSSProperties = flipped
    ? { position: 'fixed', bottom: window.innerHeight - liveRect.top + 4, left: safeLeft, zIndex: 50 }
    : { position: 'fixed', top: liveRect.bottom + 4, left: safeLeft, zIndex: 50 };

  if (!pc) return null;

  // On a public link, Save is a genuine action control that prompts sign-in
  // (mirrors every other genuine action control on this surface — see
  // PublicCampaignShell.tsx's click-interception model) rather than actually
  // persisting a selection an anonymous visitor has no account to own. The
  // in-progress selection is captured as a `save_deliverables` pending
  // action so PendingPublicActionResumeHost can replay it once the visitor
  // signs in (Task 3's registry resolves `entryUsername` back to a roster
  // entry — see pendingPublicActionRegistry.ts's `save_deliverables` handler).
  const handleSave = () => {
    if (isPublicView) {
      const entryUsername = pc.platform?.username ?? '';
      if (entryUsername && campCode && tail) {
        const action: PendingPublicAction = {
          type: 'save_deliverables',
          campCode,
          tail,
          entryUsername,
          createdAt: Date.now(),
          selectedMainIds: selectedMain ? [selectedMain] : [],
          selectedAddonIds: Array.from(selectedAddons),
        };
        requestLogin?.(action);
      } else {
        // Missing identifying context (shouldn't happen on a real share
        // link — defensive only, e.g. a malformed test render) — still open
        // the dialog, just without anything to resume.
        requestLogin?.();
      }
      return;
    }
    const rosterEntryId = pc.id ?? '';
    // Optimistically reflect ALL selected types (main + addons) so the row
    // chips stay correct while the API call and query refetch are in-flight.
    onOptimisticChange(popover.creatorId, selectedTypes);
    if (rosterEntryId) {
      selectCreatorDeliverable?.(rosterEntryId, selectedTypes).catch(() => {
        // Roll back to the persisted selection on failure.
        const persistedTypes = deliverables.filter((d) => d.is_selected).map((d) => d.type);
        onOptimisticChange(popover.creatorId, persistedTypes);
        toast.error('Failed to update deliverable selection');
      });
    }
    onClose();
  };

  // No price guard — the BE only ever sends deliverables priced >= 0 (blank /
  // negative on import are omitted), so a ₹0 deliverable is a real, selectable
  // option and must show.
  const mainDeliverables = deliverables.filter((d) => isMain(d.type, platform));
  const addonDeliverables = deliverables.filter((d) => isAddon(d.type, platform));

  return (
    <div
      ref={popoverRef}
      style={posStyle}
      className="min-w-72 rounded-xl border border-border bg-card"
      // Rendered as a sibling of the trigger, not inside it, so the shell's
      // click-interceptor (PublicCampaignShell.tsx) wouldn't otherwise reach
      // it via the trigger's own `data-public-allowed`. Safe to open fully:
      // a public visitor can select but Save prompts sign-in (handleSave
      // above), never persisting without an account; an authed read-only
      // member (`isAuthedReadOnly`) stays fully disabled with Save hidden.
      data-public-allowed-subtree=""
    >
      {/* Main deliverables — single-select checkbox: pick one, tick it again
          to clear. Mutual exclusivity is enforced by toggleMain (useDeliverableSelection),
          not by input type — visually a checkbox to match the Add-ons list below. */}
      {mainDeliverables.length > 0 && (
        <>
          <div className="border-b border-border px-3 py-2">
            <span id="commercial-popover-main-label" className="text-[11px] font-bold text-foreground">
              Deliverable
            </span>
          </div>
          <div
            role="group"
            aria-labelledby="commercial-popover-main-label"
            className="space-y-0.5 p-2"
          >
            {mainDeliverables.map((d) => {
              const selected = d.type === selectedMain;
              return (
                <button
                  key={d.type}
                  type="button"
                  role="checkbox"
                  aria-checked={selected}
                  onClick={() => !isAuthedReadOnly && toggleMain(d.type)}
                  disabled={isAuthedReadOnly}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                    isAuthedReadOnly
                      ? 'cursor-not-allowed opacity-40 text-foreground'
                      : selected
                        ? 'bg-muted text-foreground'
                        : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <span
                    className={`flex size-3.5 shrink-0 items-center justify-center rounded-sm border ${
                      selected ? 'border-border bg-foreground' : 'border-border bg-card'
                    }`}
                  >
                    {selected && <Check size={9} className="text-white" />}
                  </span>
                  <span className="flex-1 text-[12px] font-medium">{fmtDeliverableType(d.type)}</span>
                  <span className="shrink-0 text-[12px] font-bold text-foreground">
                    {d.base_price ? formatLineMoney({ amount: d.base_price, currency: d.currency ?? 'INR' }, d.converted) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Add-ons — checkboxes (gated by SHOW_DELIVERABLE_ADDONS in types.ts).
          Selected add-ons surface in the single Commercials column alongside
          the main — there is no separate Add-ons table column. */}
      {SHOW_DELIVERABLE_ADDONS && addonDeliverables.length > 0 && (
        <>
          <div className="border-y border-border px-3 py-2">
            <span id="commercial-popover-addons-label" className="text-[11px] font-bold text-muted-foreground">
              Add-ons
            </span>
            {isAddonDisabled && (
              <p className="mt-0.5 text-[10px] text-muted-foreground">Select a main deliverable first</p>
            )}
          </div>
          <div
            role="group"
            aria-labelledby="commercial-popover-addons-label"
            className="space-y-0.5 p-2"
          >
            {addonDeliverables.map((d) => {
              const checked = selectedAddons.has(d.type);
              const addonDisabled = isAddonDisabled || isAuthedReadOnly;
              const usageDays =
                d.type === 'usage_rights'
                  ? (d as DeliverableInstance & { days?: number }).days
                  : undefined;
              return (
                <button
                  key={d.type}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  onClick={() => !addonDisabled && toggleAddon(d.type)}
                  disabled={addonDisabled}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                    addonDisabled
                      ? 'cursor-not-allowed opacity-40'
                      : checked
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <span
                    className={`flex size-3.5 shrink-0 items-center justify-center rounded-sm border ${
                      checked ? 'border-border bg-foreground' : 'border-border bg-card'
                    }`}
                  >
                    {checked && <Check size={9} className="text-white" />}
                  </span>
                  <span className="flex-1 text-[12px] font-medium">
                    {fmtDeliverableType(d.type)}
                    {usageDays != null && usageDays > 0 && (
                      <span className="ml-1.5 text-[10px] text-muted-foreground">
                        · {usageDays}d
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-[12px] font-bold text-foreground">
                    {d.base_price ? formatLineMoney({ amount: d.base_price, currency: d.currency ?? 'INR' }, d.converted) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}


      {/* Total + Save */}
      <div className="flex items-center justify-between border-t border-border px-3 py-2">
        <span className="text-[12px] font-bold text-foreground">
          {totalLabel}
        </span>
        {!isAuthedReadOnly && (
          <Button
            variant="default"
            size="sm"
            type="button"
            onClick={handleSave}
            className="inline-flex items-center gap-1 rounded-full bg-foreground px-3 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-foreground disabled:opacity-40"
          >
            <Check size={11} /> Save
          </Button>
        )}
      </div>
    </div>
  );
}
